import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  LineOfCreditUpdateAutoPayResultTypenames,
  LineOfCreditUpdateAutoPaySuccess,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useNavigation,
  useRouter,
} from 'expo-router';
import LineOfCreditErrorScreenComponent, {
  VlocStepName,
} from 'features/lineOfCredit/screens/error/LineOfCreditErrorScreenComponent';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

import useSubmitLineOfCreditAutoPayStatus from './hooks/useSubmitLineOfCreditAutoPayStatus';

const AutoPayScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('vloc', {
    keyPrefix: 'autoPay',
  });

  const { bottom } = useSafeAreaInsets();

  const { autoPayEnabled } = useLocalSearchParams<{
    autoPayEnabled: string;
  }>();

  const [isAutoPayEnabled, setIsAutoPayEnabled] = useState(
    autoPayEnabled === 'true',
  );

  const routes = useNavigation().getState()?.routes;
  const prevRoute = routes ? routes[routes.length - 2] : undefined;

  const errorAnalyticsObject =
    prevRoute && prevRoute.name === 'varo-line-of-credit/manage-account'
      ? {
          errorScreenAnalyticsKey:
            '/varo-line-of-credit/autopay/errorscreen-autoPayScreen',
          errorScreenTrackingKey: 'autopay-error-tracking',
          buttonPressKey: 'manage-account-errorButtonPress',
        }
      : {
          errorScreenAnalyticsKey:
            '/varo-line-of-credit/autopay/active-view-errorscreen-autoPayScreen',
          errorScreenTrackingKey: 'active-screen-autopay-error-tracking',
        };

  const subCategory =
    prevRoute && prevRoute.name === 'varo-line-of-credit/manage-account'
      ? ''
      : 'vloc_repayment';

  const {
    result: autoPayResult,
    triggerMutation,
    fetching: isFetching,
    error: mutationError,
  } = useSubmitLineOfCreditAutoPayStatus();

  const submitAutoPay = (isEnabled: boolean) => {
    triggerMutation({ isEnabled: isEnabled });
  };

  const error =
    mutationError ||
    autoPayResult?.__typename ===
      LineOfCreditUpdateAutoPayResultTypenames.LineOfCreditUpdateAutoPayError;
  const autoPayResultSuccess =
    (autoPayResult as LineOfCreditUpdateAutoPaySuccess) ?? null;

  useEffect(() => {
    if (autoPayResultSuccess) {
      setIsAutoPayEnabled(autoPayResultSuccess.isEnabled);
    }
  }, [autoPayResultSuccess]);

  const bottomSheetRef = useRef<BottomSheetModal>(null);

  const openBottomSheet = () => {
    bottomSheetRef.current?.present();
  };

  const closeBottomSheet = () => {
    bottomSheetRef.current?.close();
  };

  useViewDidFocusScreenAnalyticsEvent({
    key: '/varo-line-of-credit/autopay-custom',
    customAttributes: {
      subcategory: subCategory,
    },
  });

  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    dismissToTab(Tabs.HOME);
  };

  const goBack = () => {
    router.back();
  };

  useBackHandler(error ? goHome : goBack);

  const autoPayTurnedOnEvent = usePressableAnalytics({
    analyticsId: 'autoPayTurnedOn',
    customAttributes: {
      subcategory: subCategory,
      account_type: 'vloc',
    },
  });

  const autoPayTurnedOffEvent = usePressableAnalytics({
    analyticsId: 'autoPayTurnedOff',
    customAttributes: {
      subcategory: subCategory,
      account_type: 'vloc',
    },
  });

  const toggleAutoPay = () => {
    if (isAutoPayEnabled) {
      autoPayTurnedOffEvent();
    } else {
      autoPayTurnedOnEvent();
    }
    submitAutoPay(!isAutoPayEnabled);
  };

  const turnAutoPayOff = () => {
    toggleAutoPay();
    closeBottomSheet();
  };

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: translatedText('header'),
          headerBackVisible: !error,
          headerShown: !error,
          gestureEnabled: !error,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScrollView contentContainerStyle={styles.scrollView}>
        <TitleLgText numberOfLines={0} style={styles.text}>
          {isAutoPayEnabled
            ? translatedText('onTitle')
            : translatedText('offTitle')}
        </TitleLgText>
        <BodyBaseText numberOfLines={0} style={styles.text}>
          {isAutoPayEnabled
            ? translatedText('onBody')
            : translatedText('offBody')}
        </BodyBaseText>
        <ScreenTransitionOverlay
          isLoading={isFetching}
          LoadingComponent={<LoadingSpinner />}
          ErrorComponent={
            error ? (
              <LineOfCreditErrorScreenComponent
                screenAnalyticsId={errorAnalyticsObject.errorScreenAnalyticsKey}
                trackingAnalyticsId={
                  errorAnalyticsObject.errorScreenTrackingKey
                }
                buttonPressKey={errorAnalyticsObject.buttonPressKey}
                trackingCustomAttributes={{
                  step_name: VlocStepName.VLOC_AUTOPAY,
                }}
              />
            ) : null
          }
        />
      </ScrollView>
      {!isFetching && !error ? (
        <View style={styles.buttonContainer}>
          {isAutoPayEnabled ? (
            <Button
              label={translatedText('offButton')}
              onPress={openBottomSheet}
              type="SECONDARY"
            />
          ) : (
            <Button
              label={translatedText('onButton')}
              onPress={toggleAutoPay}
            />
          )}
        </View>
      ) : null}
      <BottomSheet ref={bottomSheetRef}>
        <View style={[styles.bottomSheet, { paddingBottom: bottom }]}>
          <TitleMdText style={styles.bottomSheetTitle}>
            {translatedText('bottomSheetTitle')}
          </TitleMdText>
          <BodyBaseText style={styles.bottomSheetBody}>
            {translatedText('bottomSheetBody')}
          </BodyBaseText>
          <View style={styles.bottomSheetButtonContainer}>
            <Button
              label={translatedText('bottomSheetOnButton')}
              type="PRIMARY"
              onPress={closeBottomSheet}
            />
            <Button
              label={translatedText('bottomSheetOffButton')}
              type="SECONDARY"
              onPress={turnAutoPayOff}
            />
          </View>
        </View>
      </BottomSheet>
    </View>
  );
};

const styles = StyleSheet.create({
  bottomSheet: {
    paddingHorizontal: theme.spacing.m,
  },
  bottomSheetBody: {
    marginBottom: theme.spacing.m,
  },
  bottomSheetButtonContainer: {
    gap: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  bottomSheetTitle: {
    marginBottom: theme.spacing.s,
  },
  buttonContainer: {
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  text: {
    padding: theme.spacing.m,
  },
});

export default AutoPayScreen;
